<script lang="ts">
  import type { BeadColor } from '../catalog/types';
  import type { Pattern } from '../pipeline/pipeline';
  import { EMPTY } from '../pattern/empty';
  import Icon from './Icon.svelte';

  /**
   * Dock outils mobile — overlay flottant en bas du canvas.
   *
   * 2 rangs :
   *  - Palette horizontale scrollable (1 rangée de pastilles avec count)
   *  - Barre outils icon-only (Pinceau/Pot/Gomme/Main) + taille brush + Undo/Redo
   *    + bouton `⋯` sticky à droite (ouvre le MobilePanel pour les options).
   *
   * UX :
   *  - Icônes Lucide en stroke. Pas de border autour des chips (le user a
   *    explicitement demandé de retirer les borders carrés).
   *  - Active state matérialisé par fond primary bordeaux, pas par border.
   *  - Pas de duplication avec le FAB : ce dock contient UNIQUEMENT les
   *    outils d'édition. Les actions de création (importer photo, grille
   *    vierge) sont dans le FabMenu, et le bouton `⋯` ouvre les options
   *    secondaires (recadrer, export PDF, fond, etc.).
   */

  type Tool = 'paint' | 'bucket' | 'eraser' | 'pan';
  type BrushSize = 1 | 3 | 5;

  type Props = {
    pattern: Pattern;
    /** Palette dans l'ordre d'AFFICHAGE (sorted by hue). NE correspond pas à
     *  l'ordre canonique des `pattern.cells` — utiliser `canonicalIndexOf`
     *  pour récupérer l'index réel à passer à `onSelectColor`. */
    palette: ReadonlyArray<BeadColor>;
    /** Map id → index canonique dans HAMA_MIDI_COLORS (figé). Indispensable
     *  pour traduire un clic sur swatch (en ordre tri) vers l'index utilisé
     *  par activeColorIdx + pattern.cells. */
    canonicalIndexOf: ReadonlyMap<string, number>;
    activeColorIdx: number;
    tool: Tool;
    brushSize: BrushSize;
    canUndo: boolean;
    canRedo: boolean;
    onSelectColor: (idx: number) => void;
    onSelectTool: (t: Tool) => void;
    onSelectBrushSize: (s: BrushSize) => void;
    onUndo: () => void;
    onRedo: () => void;
    onExpand: () => void;
  };

  let {
    pattern,
    palette,
    canonicalIndexOf,
    activeColorIdx,
    tool,
    brushSize,
    canUndo,
    canRedo,
    onSelectColor,
    onSelectTool,
    onSelectBrushSize,
    onUndo,
    onRedo,
    onExpand,
  }: Props = $props();

  let isDrawingTool = $derived(tool === 'paint' || tool === 'eraser');
</script>

<div class="mobile-dock editor-glass" role="toolbar" aria-label="Outils d'édition mobile">
  <!-- Rang 1 : palette horizontale (entrée « Aucune perle » + couleurs). -->
  <div
    class="mobile-dock__palette editor-h-rail"
    aria-label={tool === 'eraser' ? 'Filtre couleur pour la gomme' : 'Palette de couleurs'}
  >
    <button
      type="button"
      class="editor-swatch"
      class:is-active={activeColorIdx === EMPTY}
      style="--swatch-size: 32px"
      title={tool === 'eraser'
        ? 'Aucun filtre — la gomme efface toutes les couleurs.'
        : 'Aucune perle — retire la perle. Avec le pot, vide toute la zone.'}
      aria-label={tool === 'eraser' ? 'Effacer toutes couleurs' : 'Aucune perle, mode gomme'}
      aria-pressed={activeColorIdx === EMPTY}
      onclick={() => onSelectColor(EMPTY)}
    >
      <span class="editor-swatch__dot editor-swatch__dot--empty" aria-hidden="true"></span>
      <span class="editor-swatch__count">{tool === 'eraser' ? 'toutes' : 'vide'}</span>
    </button>
    {#each palette as bead (bead.id)}
      {@const i = canonicalIndexOf.get(bead.id) ?? 0}
      {@const isActive = i === activeColorIdx}
      {@const count = pattern.counts.get(bead.id) ?? 0}
      <button
        type="button"
        class="editor-swatch"
        class:is-active={isActive}
        style="--swatch-size: 32px"
        title={`${bead.name} — ${count} perles`}
        aria-label={`${bead.name}, ${count} perles`}
        aria-pressed={isActive}
        onclick={() => onSelectColor(i)}
      >
        <span
          class="editor-swatch__dot"
          style={`--bead-color: rgb(${bead.rgb.r}, ${bead.rgb.g}, ${bead.rgb.b})`}
        ></span>
        <span class="editor-swatch__count">{count}</span>
      </button>
    {/each}
  </div>

  <!-- Rang 2 : outils icon-only, scrollable + `⋯` sticky à droite -->
  <div class="mobile-dock__row">
    <div class="mobile-dock__tools-rail editor-h-rail">
      <div class="mobile-dock__group" role="group" aria-label="Outil">
        <button
          type="button"
          class="dock-icon-btn"
          class:dock-icon-btn--active={tool === 'paint'}
          aria-pressed={tool === 'paint'}
          aria-label="Pinceau"
          title="Pinceau"
          onclick={() => onSelectTool('paint')}
        >
          <Icon name="brush" size={22} />
        </button>
        <button
          type="button"
          class="dock-icon-btn"
          class:dock-icon-btn--active={tool === 'bucket'}
          aria-pressed={tool === 'bucket'}
          aria-label="Pot de peinture"
          title="Pot de peinture"
          onclick={() => onSelectTool('bucket')}
        >
          <Icon name="bucket" size={22} />
        </button>
        <button
          type="button"
          class="dock-icon-btn"
          class:dock-icon-btn--active={tool === 'eraser'}
          aria-pressed={tool === 'eraser'}
          aria-label="Gomme"
          title="Gomme"
          onclick={() => onSelectTool('eraser')}
        >
          <Icon name="eraser" size={22} />
        </button>
        <button
          type="button"
          class="dock-icon-btn"
          class:dock-icon-btn--active={tool === 'pan'}
          aria-pressed={tool === 'pan'}
          aria-label="Déplacer"
          title="Déplacer"
          onclick={() => onSelectTool('pan')}
        >
          <Icon name="hand" size={22} />
        </button>
      </div>

      {#if isDrawingTool}
        <span class="mobile-dock__sep" aria-hidden="true"></span>
        <div class="mobile-dock__group" role="group" aria-label="Taille du pinceau">
          {#each [1, 3, 5] as s (s)}
            <button
              type="button"
              class="dock-size-btn"
              class:dock-size-btn--active={brushSize === s}
              aria-pressed={brushSize === s}
              title={`${s}×${s} perles`}
              onclick={() => onSelectBrushSize(s as BrushSize)}
            >
              {s}
            </button>
          {/each}
        </div>
      {/if}

      <span class="mobile-dock__sep" aria-hidden="true"></span>

      <div class="mobile-dock__group" role="group" aria-label="Historique">
        <button
          type="button"
          class="dock-icon-btn"
          disabled={!canUndo}
          aria-label="Annuler"
          title="Annuler"
          onclick={onUndo}
        >
          <Icon name="undo" size={22} />
        </button>
        <button
          type="button"
          class="dock-icon-btn"
          disabled={!canRedo}
          aria-label="Rétablir"
          title="Rétablir"
          onclick={onRedo}
        >
          <Icon name="redo" size={22} />
        </button>
      </div>
    </div>

    <!-- Bouton « Plus » sticky à droite. Sans border, juste accent au survol -->
    <button
      type="button"
      class="dock-icon-btn dock-icon-btn--more"
      title="Plus d'options"
      aria-label="Ouvrir le panneau d'options"
      onclick={onExpand}
    >
      <Icon name="more" size={22} />
    </button>
  </div>
</div>

<style>
  .mobile-dock {
    position: absolute;
    left: var(--space-2);
    right: var(--space-2);
    bottom: var(--space-2);
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    border-radius: var(--radius-md);
    padding: 0;
    z-index: 20;
  }

  .mobile-dock__row {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding-block: var(--space-1);
    padding-inline: var(--space-2);
  }
  .mobile-dock__tools-rail {
    flex: 1;
    min-width: 0;
    padding-inline: 0;
  }
  .mobile-dock__group {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
  }
  .mobile-dock__sep {
    width: 1px;
    height: 22px;
    background: var(--color-border);
    flex-shrink: 0;
    margin-inline: var(--space-1);
  }

  /* --- Bouton icone seul (outil, undo/redo, more) ---
     PAS de border carrée (correction demandée). Active state matérialisé
     par fond primary + icon en cream. Hover → fond surface léger. */
  .dock-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: transparent;
    border: 0;
    border-radius: var(--radius-md);
    color: var(--color-text);
    cursor: pointer;
    flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
    transition: background-color 0.12s ease, color 0.12s ease;
  }
  @media (hover: hover) {
    .dock-icon-btn:hover:not(:disabled):not(.dock-icon-btn--active) {
      background: color-mix(in srgb, var(--color-text) 8%, transparent);
    }
  }
  .dock-icon-btn--active {
    background: var(--color-primary);
    color: var(--color-background);
  }
  .dock-icon-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  .dock-icon-btn:focus {
    outline: none;
  }
  .dock-icon-btn:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }
  .dock-icon-btn--more {
    color: var(--color-accent);
  }

  /* --- Taille de brush (1/3/5) : pastille texte, même approche borderless --- */
  .dock-size-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    padding-inline: var(--space-2);
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: var(--color-text-muted);
    font: inherit;
    font-size: var(--text-small);
    font-weight: 600;
    cursor: pointer;
    flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
    transition: background-color 0.12s ease, color 0.12s ease;
  }
  @media (hover: hover) {
    .dock-size-btn:hover:not(.dock-size-btn--active) {
      background: color-mix(in srgb, var(--color-text) 8%, transparent);
      color: var(--color-text);
    }
  }
  .dock-size-btn--active {
    background: var(--color-primary);
    color: var(--color-background);
  }
</style>
